import React, { useState, useCallback, useMemo, memo, useEffect } from "react";
import {
  Input,
  Button,
  Card,
  Spin,
  message,
  Modal,
  Select,
  Avatar,
  Empty,
  Tag,
  Space,
  Divider,
  Row,
  Col,
  Tabs,
  Pagination,
  Form,
  Upload,
  List,
  Popconfirm,
} from "antd";
import {
  SearchOutlined,

  TeamOutlined,
  PlusOutlined,

} from "@ant-design/icons";
import drEasyApi from "../../../Api/Customapi";

import { FaUserMd } from "react-icons/fa";
import DoctorPerformance from "./DoctorPerformance";
import ConsultantCard from "./ConsultantCard";
import SpecialistCard from "./SpecialistCard";
import { ConsultantFormModal, SpecialistFormModal } from "./Modals";
import { useCheckDoctorVacation } from "../../../hooks/Usevacations";
  const role=localStorage.getItem("role");


const consultantApis = {
  getList: async (pageNumber = 1, pageSize = 20) => {
    const response = await drEasyApi.get("/api/Centers/getMyDoctors", {
      params: { PageNumber: pageNumber, PageSize: pageSize },
    });
    return response.data;
  },
  add: async (data) => {
    const response = await drEasyApi.post("/api/Centers/AddDoctor", data);
    return response.data;
  },
  edit: async (data) => {
    const response = await drEasyApi.put("/api/Doctor/Edit", data);
    return response.data;
  },
  delete: async (id) => {
    const response = await drEasyApi.delete(`/api/Doctor/DeleteDoctor?id=${id}`);
    return response.data;
  },
};

const specialistApis = {
  getList: async (pageNumber = 1, pageSize = 20) => {
    const response = await drEasyApi.get("/api/Centers/getMySpecialists", {
      params: { PageNumber: pageNumber, PageSize: pageSize },
    });
    return response.data;
  },
  add: async (data) => {
    const response = await drEasyApi.post("/api/Centers/AddSpecialist", data);
    return response.data;
  },
  edit: async (data) => {
    const response = await drEasyApi.put("/api/Centers/EditSpecialist", data);
    return response.data;
  },
  delete: async (id) => {
    const response = await drEasyApi.delete("/api/Centers/DeleteSpecialist", {
      params: { id },
    });
    return response.data;
  },
};

const getSpecializations = async () => {
  const response = await drEasyApi.get("/api/Specialization/GetSpecializations");
  return response.data;
};


const ConsultantCardWithVacation = memo(({
  item,
  index,
  isMobile,
  onClick,
  onEdit,
  onDelete,
  deleteLoading,
}) => {
  const { data: vacationData } = useCheckDoctorVacation(item.doctorId);

  return (
    <ConsultantCard
      key={item.doctorId}
      item={item}
      index={index}
      isMobile={isMobile}
      onClick={onClick}
      role={role}
      onEdit={onEdit}
      onDelete={onDelete}
      deleteLoading={deleteLoading}
      isOnVacation={vacationData?.isOnVacation ?? false}
      vacationInfo={vacationData ?? null}
    />
  );
});
ConsultantCardWithVacation.displayName = "ConsultantCardWithVacation";

const useResponsive = () => {
  const [breakpoint, setBreakpoint] = useState("lg");
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setBreakpoint("sm");
        setIsMobile(true);
        setIsTablet(false);
      } else if (width < 768) {
        setBreakpoint("md");
        setIsMobile(false);
        setIsTablet(true);
      } else if (width < 1024) {
        setBreakpoint("lg");
        setIsMobile(false);
        setIsTablet(true);
      } else if (width < 1280) {
        setBreakpoint("xl");
        setIsMobile(false);
        setIsTablet(false);
      } else {
        setBreakpoint("2xl");
        setIsMobile(false);
        setIsTablet(false);
      }
    };

    updateBreakpoint();
    window.addEventListener("resize", updateBreakpoint);
    return () => window.removeEventListener("resize", updateBreakpoint);
  }, []);

  return { breakpoint, isMobile, isTablet };
};


const SearchFilter = memo(({ value, onChange, placeholder, isMobile }) => (
 
    <Input
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      prefix={<SearchOutlined className="text-gray-400" />}
      size="large"
      className="rounded-lg w-80"
      allowClear
    />

));
SearchFilter.displayName = "SearchFilter";

const ConsultantsTab = ({ isMobile, isTablet }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [specializations, setSpecializations] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [performanceModalVisible, setPerformanceModalVisible] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);

  const fetchData = useCallback(async (pageNumber = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const [response, specs] = await Promise.all([
        consultantApis.getList(pageNumber, pageSize),
        getSpecializations(),
      ]);
      setData(response.doctors || []);
      setSpecializations(specs);
      setPagination({
        current: response.pageNumber,
        pageSize: response.pageSize,
        total: response.totalCount,
      });
    } catch (error) {
      console.error("Error fetching consultants:", error);
      message.error("Failed to fetch consultants");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const filtered = searchText
      ? data.filter((item) => item.name.toLowerCase().includes(searchText.toLowerCase()))
      : data;
    setFilteredData(filtered);
  }, [data, searchText]);

  const handleAdd = useCallback(() => { setEditingItem(null); setShowModal(true); }, []);
  const handleEdit = useCallback((item) => { setEditingItem(item); setShowModal(true); }, []);

  const handleDelete = useCallback(async (id) => {
    setDeleteLoading(id);
    try {
      await consultantApis.delete(id);
      message.success("Consultant deleted successfully");
      fetchData(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(error.response?.data[0] || error.message || "Failed to delete consultant");
    } finally {
      setDeleteLoading(null);
    }
  }, [pagination, fetchData]);
const handleSubmit = useCallback(async (itemData) => {
  setModalLoading(true);
  try {
    let result;
    if (editingItem) {
      result = await consultantApis.edit(itemData);
      message.success("Consultant updated successfully");
    } else {
      result = await consultantApis.add(itemData);  // 👈 capture response
      message.success("Consultant added successfully");
    }
    setShowModal(false);
    fetchData(pagination.current, pagination.pageSize);
    return result; // 👈 must return so modal can open Step 2
  } catch (error) {
    message.error(error.response?.data[0] || error.message || "Operation failed");
  } finally {
    setModalLoading(false);
  }
}, [editingItem, pagination, fetchData]);

  const handleClick = useCallback((item) => {
    setSelectedDoctor(item);
    setPerformanceModalVisible(true);
  }, []);

  return (
    <>
      <div className="flex flex-wrap gap-3 justify-center md:justify-between items-center mb-3">
        <h3 className="text-lg font-semibold">My Consultants</h3>
          <SearchFilter
        value={searchText}
        onChange={setSearchText}
        placeholder="Search by consultant name..."
        isMobile={isMobile}
      />
     {role!=7&&   <Button
          type=""
          icon={<PlusOutlined />}
          onClick={handleAdd}
          size={isMobile ? "middle" : "large"}
          className="bg-gradient-to-r text-white from-primary to-primary_hover hover:bg-primary_hover hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
        >
          {isMobile ? "Add" : "Add Consultant"}
        </Button>}
      </div>

    

      <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-sm">
        {loading ? (
          <div className="text-center py-16">
            <Spin size="large" />
            <div className="mt-4 text-gray-500">Loading consultants...</div>
          </div>
        ) : filteredData.length > 0 ? (
          <>
            

            <div
              className={`grid grid-cols-1 ${
                isMobile
                  ? "gap-4"
                  : isTablet
                  ? "sm:grid-cols-2 gap-4 sm:gap-6"
                  : "sm:grid-cols-2 lg:grid-cols-3 gap-6"
              }`}
            >
              {filteredData.map((item, index) => (
                // Each card manages its own vacation status via the hook internally
                <ConsultantCardWithVacation
                  key={item.doctorId}
                  item={item}
                  index={index}
                  isMobile={isMobile}
                  onClick={handleClick}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  deleteLoading={deleteLoading === item.doctorId}
                  role={role}
                />
              ))}
            </div>

            {pagination.total > pagination.pageSize && (
              <div className="mt-8 flex justify-center">
                <Pagination
                  current={pagination.current}
                  total={pagination.total}
                  pageSize={pagination.pageSize}
                  onChange={(page, pageSize) => fetchData(page, pageSize)}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} consultants`}
                  className="bg-white rounded-lg p-4 shadow-sm"
                />
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span className={`text-gray-500 ${isMobile ? "text-sm" : ""}`}>
                  {searchText ? "No consultants found matching your search" : "No consultants found"}
                </span>
              }
            >
             {role!=7&& <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                className="bg-gradient-to-r from-green-500 to-green-600 border-none rounded-xl"
              >
                Add First Consultant
              </Button>}
            </Empty>
          </div>
        )}
      </Card>

      <ConsultantFormModal
        visible={showModal}
        onCancel={() => setShowModal(false)}
        onSubmit={handleSubmit}
        item={editingItem}
        loading={modalLoading}
        specializations={specializations}
      />

      <DoctorPerformance
        visible={performanceModalVisible}
        onClose={() => setPerformanceModalVisible(false)}
        doctor={selectedDoctor}
      />
    </>
  );
};


const SpecialistsTab = ({ isMobile }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [specializations, setSpecializations] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [performanceModalVisible, setPerformanceModalVisible] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);

  const fetchData = useCallback(async (pageNumber = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const [response, specs] = await Promise.all([
        specialistApis.getList(pageNumber, pageSize),
        getSpecializations(),
      ]);
      setData(response.specialists || []);
      setSpecializations(specs);
      setPagination({ current: pageNumber, pageSize, total: response.totalCount || 0 });
    } catch (error) {
      console.error("Error fetching specialists:", error);
      if (error.response?.status === 403) {
        window.location.href = "/sub";
      } else {
        message.error("Failed to fetch specialists");
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const filtered = searchText
      ? data.filter((item) => item.name.toLowerCase().includes(searchText.toLowerCase()))
      : data;
    setFilteredData(filtered);
  }, [data, searchText]);

  const handleAdd = useCallback(() => { setEditingItem(null); setShowModal(true); }, []);
  const handleEdit = useCallback((item) => { setEditingItem(item); setShowModal(true); }, []);

  const handleDelete = useCallback(async (id) => {
    setDeleteLoading(id);
    try {
      await specialistApis.delete(id);
      message.success("Specialist deleted successfully");
      fetchData(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(error.response?.data[0] || error.message || "Failed to delete specialist");
    } finally {
      setDeleteLoading(null);
    }
  }, [pagination, fetchData]);

  const handleSubmit = useCallback(async (itemData) => {
    setModalLoading(true);
    try {
      if (editingItem) {
        await specialistApis.edit(itemData);
        message.success("Specialist updated successfully");
      } else {
        await specialistApis.add(itemData);
        message.success("Specialist added successfully");
      }
      setShowModal(false);
      fetchData(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(error.response?.data[0] || error.message || "Operation failed");
    } finally {
      setModalLoading(false);
    }
  }, [editingItem, pagination, fetchData]);

  const handleClick = useCallback((item) => {
    setSelectedDoctor({ doctorId: item.specialistId, name: item.name });
    setPerformanceModalVisible(true);
  }, []);

  return (
    <>
      <div className="flex flex-wrap justify-center md:justify-between items-center mb-3 gap-3 ">
        <h3 className="text-lg font-semibold">My Specialists</h3>
          <SearchFilter
        value={searchText}
        onChange={setSearchText}
        placeholder="Search by specialist name..."
        isMobile={isMobile}
      />
      {role!=7&&
        <Button
          type=""
          className="bg-gradient-to-r text-white from-primary to-primary_hover hover:bg-primary_hover hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          size={isMobile ? "middle" : "large"}
        >
          {isMobile ? "Add" : "Add Specialist"}
        </Button>}
      </div>

    

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <Spin size="large" />
        </div>
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 2, md: 2, lg: 3, xl: 4, xxl: 4 }}
          dataSource={filteredData}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  searchText
                    ? "No specialists found matching your search"
                    : "No specialists found"
                }
              >
             {role!=7 &&  <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAdd}
                  className="bg-[#298d9f]"
                >
                  Add First Specialist
                </Button>}
              </Empty>
            ),
          }}
          renderItem={(item, index) => (
            <List.Item>
              <SpecialistCard
                item={item}
                index={index}
                isMobile={isMobile}
                onClick={handleClick}
                onEdit={handleEdit}
                onDelete={handleDelete}
                deleteLoading={deleteLoading === item.specialistId}
                
                
              />
            </List.Item>
          )}
        />
      )}

      <SpecialistFormModal
        visible={showModal}
        onCancel={() => setShowModal(false)}
        onSubmit={handleSubmit}
        item={editingItem}
        loading={modalLoading}
        specializations={specializations}
      />

      <DoctorPerformance
        visible={performanceModalVisible}
        onClose={() => setPerformanceModalVisible(false)}
        doctor={selectedDoctor}
        spec={true}
      />
    </>
  );
};


const MedicalStaffScreen = () => {
  const { isMobile, isTablet } = useResponsive();
  const [activeTab, setActiveTab] = useState("consultants");
  

  const tabItems = [
    {
      key: "consultants",
      label: (
        <span className="flex items-center gap-2">
          <TeamOutlined />
          Consultants
        </span>
      ),
      children: <ConsultantsTab isMobile={isMobile} isTablet={isTablet} />,
    },
    {
      key: "specialists",
      label: (
        <span className="flex items-center gap-2">
          <FaUserMd />
          Specialists
        </span>
      ),
      children: <SpecialistsTab isMobile={isMobile} />,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <style jsx>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 640px) {
          .ant-card-body { padding: 16px !important; }
        }
      `}</style>

      <div className={`${isMobile ? "p-0" : "p-2"}`}>
        <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-sm mt-6">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            size={isMobile ? "small" : "large"}
            className="medical-staff-tabs"
          />
        </Card>
      </div>
    </div>
  );
};

export default MedicalStaffScreen;